import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {BehaviorSubject, Observable, delay, map, mergeMap, of, retry, tap, forkJoin, shareReplay} from 'rxjs';

import { ExportParameter } from '@/silverfin-redirect/components';
import { TaxRole } from '@/tax-detail/models';
import {
    BulkImport,
    ExportType,
    PersonalTaxExport,
    PersonalTaxFile,
    PersonalTaxImport,
    PersonalTaxLink,
    PersonalTaxOperation,
    PersonalTaxParsing,
    PersonalTaxRequest,
    PollingStatus,
    TaxNotification,
} from '@/tax/models';
import { CompanyLinkSync } from '@/tax/models/company-link-sync.model';

import { AuthService } from './auth.service';

import { environment } from '@env';
import {catchError} from "rxjs/operators";

const DELAY_STATUS_FETCH_MS = 1000;
const LONG_DELAY_STATUS_FETCH_MS = 5000;

const invalidErrorMessage = 'Not ready';

@Injectable({
    providedIn: 'root',
})
export class PersonalTaxService {
    readonly activePersonalTaxFile = new BehaviorSubject<PersonalTaxFile | undefined>(undefined);

    constructor(private http: HttpClient, private authService: AuthService) {}

    /**
     * Retrieve all organisation personal tax files
     * @param orgId
     *
     * @returns an observable of a list of the personal tax files
     */
    listPersonalTaxFiles(orgId: number, year?: number, companyId?: number): Observable<PersonalTaxFile[]> {
        let paramsContent = {} as Record<string, number>;
        if (year !== undefined) {
            paramsContent['year'] = year;
        }
        if (companyId !== undefined) {
            paramsContent['company'] = companyId;
        }
        const params = Object.keys(paramsContent).length > 0 ? { params: paramsContent } : {};
        return this.http.get<PersonalTaxFile[]>(environment.endpoints.organisationPersonalTaxList(orgId), params).pipe(
            map((files) => {
                let personalTaxFiles = files.map((file: any) => new PersonalTaxFile().deserialize(file));
                if ((this.authService.activeUser.getValue()?.isStaff ?? false) && !this.authService.adminMode.getValue()) {
                    personalTaxFiles = personalTaxFiles.filter((personalTaxFile) =>
                        this.authService.activeUser.getValue()?.isCompanyViewer(personalTaxFile.organisation, personalTaxFile.company.id),
                    );
                }
                return personalTaxFiles;
            }),
        );
    }

    /**
     * Retrieve all organisation personal tax files years
     * @param orgId
     *
     * @returns an observable of a list of the personal tax file years (income year)
     */
    getPersonalTaxFileYears(orgId: number): Observable<{ years: number[] }> {
        return this.http.get<{ years: number[] }>(environment.endpoints.organisationPersonalTaxYears(orgId));
    }

    /**
     * Create/Retrieve a personal tax file based on Silverfin identifiers
     * @param orgId the id of the organisation
     * @param firmId the id of the silverfin firm
     * @param companyId the id of the silverfin company
     * @param periodId the id of the silverfin period
     * @param reconciliationId the id of the user questionnaire of a personal tax workflow
     *
     * @returns an observable for a PersonalTaxFile
     */
    createPersonalTaxFile(
        orgId: number,
        firmId: number,
        companyId: number,
        periodId: number,
        reconciliationId: number,
    ): Observable<PersonalTaxFile> {
        return this.http
            .post<PersonalTaxFile>(environment.endpoints.organisationPersonalTaxList(orgId), {
                sf_firm_id: firmId,
                sf_company_id: companyId,
                period_id: periodId,
                reconciliation_id: reconciliationId,
            })
            .pipe(map((file) => new PersonalTaxFile().deserialize(file)));
    }

    getPersonalTaxFile(orgId: number, fileId: number): Observable<PersonalTaxFile> {
        return this.http
            .get<PersonalTaxFile>(environment.endpoints.organisationPersonalTaxDetails(orgId, fileId))
            .pipe(map((newFile) => new PersonalTaxFile().deserialize(newFile)));
    }

    /**
     * Update a personal tax file
     * @param orgId the id of the organsation
     * @param personalTaxFile the updated personal tax file
     *
     * @return An observable of the new Personal tax file
     */
    updatePersonalTaxFile(orgId: number, personalTaxFile: PersonalTaxFile): Observable<PersonalTaxFile> {
        return this.http
            .patch<PersonalTaxFile>(
                environment.endpoints.organisationPersonalTaxDetails(orgId, personalTaxFile.id),
                personalTaxFile.serialize(),
            )
            .pipe(map((newPersonalTaxFile) => new PersonalTaxFile().deserialize(newPersonalTaxFile)));
    }

    /**
     * Starts an optimization on tax-on-web
     * @param orgId the id of the organisation
     * @param personalTaxId the id of the PersonalTaxFile
     *
     * @return an observable for the created PersonalTaxOperation
     */
    createOptimization(orgId: number, personalTaxId: number): Observable<PersonalTaxOperation> {
        return this.http
            .post<PersonalTaxOperation>(environment.endpoints.organisationPersonalTaxOptimizationList(orgId, personalTaxId), {})
            .pipe(map((calculation) => new PersonalTaxOperation().deserialize(calculation)));
    }

    /**
     * Retrieve a optimization of tax-on-web
     * @param orgId the id of the organisation
     * @param personalTaxId the id of the PersonalTaxFile
     * @param operationId the id of the Operation
     *
     * @return an observable for the created PersonalTaxOperation
     */
    getOptimization(orgId: number, personalTaxId: number, operationId: number): Observable<PersonalTaxOperation> {
        return this.http
            .get<PersonalTaxOperation>(environment.endpoints.organisationPersonalTaxOptimizationDetail(orgId, personalTaxId, operationId))
            .pipe(map((optimization) => new PersonalTaxOperation().deserialize(optimization)));
    }

    /**
     * Starts a calculation on tax-on-web
     * @param orgId the id of the organisation
     * @param personalTaxId the id of the PersonalTaxFile
     *
     * @return an observable for the created PersonalTaxOperation
     */
    createCalculation(orgId: number, personalTaxId: number): Observable<PersonalTaxOperation> {
        return this.http
            .post<PersonalTaxOperation>(environment.endpoints.organisationPersonalTaxCalculationList(orgId, personalTaxId), {})
            .pipe(map((calculation) => new PersonalTaxOperation().deserialize(calculation)));
    }

    /**
     * Retrieve a calculation of tax-on-web
     * @param orgId the id of the organisation
     * @param personalTaxId the id of the PersonalTaxFile
     * @param operationId the id of the Calculation
     *
     * @return an observable for the created PersonalTaxCalculation
     */
    getCalculation(orgId: number, personalTaxId: number, operationId: number): Observable<PersonalTaxOperation> {
        return this.http
            .get<PersonalTaxOperation>(
                environment.endpoints.organisationPersonalTaxCalculationDetail(orgId, personalTaxId, operationId),
                {},
            )
            .pipe(map((calculation) => new PersonalTaxOperation().deserialize(calculation)));
    }

    /**
     * Retrieve a personal tax import
     * @param orgId the id of the organisation
     * @param personalTaxId the id of the PersonalTaxFile
     * @param importId the id of an import that needs to be retrieved
     *
     * @returns An observable for a PersonalTaxImport
     */
    getImport(orgId: number, personalTaxId: number, importId: number): Observable<PersonalTaxImport> {
        return this.http
            .get<PersonalTaxImport>(environment.endpoints.organisationPersonalTaxImportDetails(orgId, personalTaxId, importId))
            .pipe(map((imp) => new PersonalTaxImport().deserialize(imp)));
    }

    /**
     * Starts an import of data on tax-on-web
     * @param taxFiles an array of PersonalTaxFiles
     *
     * @return an observable of an array of the created PersonalTaxImports
     */
    createImports(taxFiles: PersonalTaxFile[]): Observable<PersonalTaxImport[]> {
        const payloads = taxFiles.map(taxFile => ({
            personal_tax_file: taxFile.id,
            social_security_number: taxFile.company.socialSecurityNumber,
            language: taxFile.company.language,
        }));
        return this.http
            .post<PersonalTaxImport[]>(environment.endpoints.organisationPersonalTaxImportList(taxFiles[0].organisation), payloads)
            .pipe(
                map((results) => results.map(ptImport => new PersonalTaxImport().deserialize(ptImport))),
            );
    }

    /**
     * Retrieve a personal tax export
     * @param orgId the id of the organisation
     * @param personalTaxId the id of the PersonalTaxFile
     * @param exportId the id of an export that needs to be retrieved
     *
     * @returns An observable for a PersonalTaxImport
     */
    getExport(orgId: number, personalTaxId: number, exportId: number): Observable<PersonalTaxExport> {
        return this.http
            .get<PersonalTaxExport>(environment.endpoints.organisationPersonalTaxExportDetails(orgId, personalTaxId, exportId))
            .pipe(map((exp) => new PersonalTaxExport().deserialize(exp)));
    }

    /**
     * Starts an export of data on tax-on-web
     * @param orgId the id of the organisation
     * @param personalTaxId the id of the PersonalTaxFile
     *
     * @return an observable for the created PersonalTaxExport
     */
    createExport(orgId: number, personalTaxId: number, type: ExportType): Observable<PersonalTaxExport> {
        return this.http
            .post<PersonalTaxExport>(environment.endpoints.organisationPersonalTaxExportList(orgId, personalTaxId), { type: type })
            .pipe(map((exp) => new PersonalTaxExport().deserialize(exp)));
    }

    /**
     * Retrieve a personal tax bulk export
     * @param orgId the id of the organisation
     * @param exportId the id of the bulk export that needs to be retrieved
     *
     * @returns An observable for a PersonalTaxImport
     */
    getBulkExport(orgId: number, exportId: number): Observable<PersonalTaxExport> {
        return this.http
            .get<PersonalTaxExport>(environment.endpoints.organisationPersonalTaxBulkExportDetails(orgId, exportId))
            .pipe(map((exp) => new PersonalTaxExport().deserialize(exp)));
    }

    /**
     * Starts a bulk-export of data on tax-on-web
     * @param orgId the id of the organisation
     * @param data list of objects containing the company id, the period id, the reconciliation_id and the firm_id
     *
     * @return an observable for the created PersonalTaxExport
     */
    createBulkExport(orgId: number, data: ExportParameter[], type: ExportType): Observable<PersonalTaxExport> {
        return this.http
            .post<PersonalTaxExport>(environment.endpoints.organisationPersonalTaxBulkExportList(orgId), {
                export_parameters: data,
                type: type,
            })
            .pipe(map((exp) => new PersonalTaxExport().deserialize(exp)));
    }

    /**
     * Starts a bulk-export of data on tax-on-web
     * @param orgId the id of the organisation
     * @param formData form data containing type (ImportType), override (boolean) and files
     *
     * @return an observable for the created BulkImport
     */
    createBulkImport(orgId: number, formData: FormData): Observable<BulkImport> {
        return this.http
            .post<BulkImport>(environment.endpoints.organisationPersonalTaxBulkImportList(orgId), formData)
            .pipe(map((bi) => new BulkImport().deserialize(bi)));
    }

    /**
     * Starts a bulk-export of data on tax-on-web
     * @param orgId the id of the organisation
     * @param importId the id of the bulk import
     *
     * @return an observable for the created BulkImport
     */
    getBulkImport(orgId: number, importId: number): Observable<BulkImport> {
        return this.http
            .get<BulkImport>(environment.endpoints.organisationPersonalTaxBulkImportDetail(orgId, importId))
            .pipe(map((bi) => new BulkImport().deserialize(bi)));
    }

    /**
     * Retrieve a personal tax bulk parsing
     * @param orgId the id of the organisation
     * @param personalTaxId the id of the personal tax file
     * @param parsingId the id of the parsing that needs to be retrieved
     *
     * @returns An observable for a PersonalTaxParsing
     */
    getParsing(orgId: number, personalTaxId: number, parsingId: number): Observable<PersonalTaxParsing> {
        return this.http
            .get<PersonalTaxParsing>(environment.endpoints.organisationPersonalTaxParsingDetails(orgId, personalTaxId, parsingId))
            .pipe(map((parsing) => new PersonalTaxParsing().deserialize(parsing)));
    }

    /**
     * Send a notification to a user using their email. The user will be added to silverfin if he/she has no account yet
     * @param orgId the id of the organisation
     * @param personalTaxId the id of the PersonalTaxFile
     * @param role the role of the user to be notified about this personal tax file (optional)
     */
    sendQuestionnaireResult(orgId: number, personalTaxId: number, role?: TaxRole): Observable<TaxNotification> {
        return this.http
            .post<any>(environment.endpoints.organisationPersonalTaxNotificationList(orgId, personalTaxId), { role })
            .pipe(map((notification) => new TaxNotification().deserialize(notification)));
    }

    getQuestionnaireResult(orgId: number, personalTaxId: number, notificationId: number): Observable<TaxNotification> {
        return this.http
            .get<any>(environment.endpoints.organisationPersonalTaxNotificationDetails(orgId, personalTaxId, notificationId))
            .pipe(map((notification) => new TaxNotification().deserialize(notification)));
    }

    /**
     * Parses a file
     * @param orgId the id of the organisation
     * @param personalTaxId the id of the PersonalTaxFile
     * @param fileFormData the file that needs parsing
     *
     * @return an empty observable
     */
    createParsing(orgId: number, personalTaxId: number, fileFormData: FormData): Observable<PersonalTaxParsing> {
        return this.http
            .post<any>(environment.endpoints.organisationPersonalTaxParsingsList(orgId, personalTaxId), fileFormData)
            .pipe(map((parsing) => new PersonalTaxParsing().deserialize(parsing)));
    }

    /**
     * List all links of a personal tax file
     * @param orgId the id of the organsation
     * @param personalTaxId the id of the personal tax file
     *
     * @return An observable of the list of PersonalTaxLink's
     */
    listLinks(orgId: number, personalTaxId: number): Observable<PersonalTaxLink[]> {
        return this.http
            .get<PersonalTaxLink[]>(environment.endpoints.organisationPersonalTaxLinkList(orgId, personalTaxId))
            .pipe(map((links) => links.map((link) => new PersonalTaxLink().deserialize(link))));
    }

    /**
     * Update a personal tax link
     * @param orgId the id of the organsation
     * @param personalTaxId the id of the personal tax file
     * @param link the updated link data
     *
     * @return An observable of the new Personal tax link
     */
    updateLink(orgId: number, personalTaxId: number, link: PersonalTaxLink): Observable<PersonalTaxLink> {
        return this.http
            .patch<PersonalTaxLink>(
                environment.endpoints.organisationPersonalTaxLinkDetails(orgId, personalTaxId, link.id),
                link.serialize(),
            )
            .pipe(map((newLink) => new PersonalTaxLink().deserialize(newLink)));
    }

    /**
     * Create a personal tax link
     * @param orgId the id of the organsation
     * @param personalTaxId the id of the personal tax file
     * @param link the updated link data
     *
     * @return An observable of the new Personal tax link
     */
    createLink(orgId: number, personalTaxId: number, link: PersonalTaxLink): Observable<PersonalTaxLink> {
        return this.http
            .post<PersonalTaxLink>(environment.endpoints.organisationPersonalTaxLinkList(orgId, personalTaxId), link.serialize())
            .pipe(map((newLink) => new PersonalTaxLink().deserialize(newLink)));
    }

    /**
     * Delete a personal tax link
     * @param orgId the id of the organsation
     * @param personalTaxId the id of the personal tax file
     * @param linkId the id of the link
     */
    deleteLink(orgId: number, personalTaxId: number, linkId: number): Observable<any> {
        return this.http.delete<any>(environment.endpoints.organisationPersonalTaxLinkDetails(orgId, personalTaxId, linkId));
    }

    /**
     * Sync links to Silverfin
     * @param orgId the id of the organsation
     * @param personalTaxId the id of the personal tax file
     *
     * @returns An observable of the Company Link Sync
     */
    synchronizeLinks(orgId: number, personalTaxId: number): Observable<CompanyLinkSync> {
        return this.http
            .post<CompanyLinkSync>(environment.endpoints.organisationPersonalTaxLinkSync(orgId, personalTaxId), {})
            .pipe(map((newLink) => new CompanyLinkSync().deserialize(newLink)));
    }

    /**
     * Get a link sync object
     * @param orgId the id of the organsation
     * @param personalTaxId the id of the personal tax file
     * @param syncId the id of the link sync object
     *
     * @returns An observable of the Company Link Sync
     */
    getLinksSync(orgId: number, personalTaxId: number, syncId: number): Observable<CompanyLinkSync> {
        return this.http
            .get<CompanyLinkSync>(environment.endpoints.organisationPersonalTaxLinkSyncDetails(orgId, personalTaxId, syncId), {})
            .pipe(map((newLink) => new CompanyLinkSync().deserialize(newLink)));
    }

    /**
     * Sync links and poll result until it is ready
     * @param orgId id of the organisation
     * @param personalTaxId id of the personal tax file
     *
     * @returnss Observable of links sync that is ready
     */
    synchronizeLinksAndPollResult(orgId: number, personalTaxId: number): Observable<CompanyLinkSync> {
        return this.synchronizeLinks(orgId, personalTaxId).pipe(
            delay(DELAY_STATUS_FETCH_MS),
            mergeMap((newSync) =>
                this.getLinksSync(orgId, personalTaxId, newSync.id).pipe(
                    tap((sync) => {
                        if (sync.status === PollingStatus.Init || sync.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    /**
     * Create new calculation and poll result until it is ready
     * @param orgId id of the organisation
     * @param personalTaxId id of the personal tax file
     *
     * @returns Observable of calculation that is ready
     */
    createCalculationAndPollResult(orgId: number, personalTaxId: number): Observable<PersonalTaxOperation> {
        return this.createCalculation(orgId, personalTaxId).pipe(
            delay(DELAY_STATUS_FETCH_MS),
            mergeMap((newCalculation) =>
                this.getCalculation(orgId, personalTaxId, newCalculation.id).pipe(
                    tap((calculation) => {
                        if (calculation.status === PollingStatus.Init || calculation.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    /**
     * Create new calculation and poll result until it is ready
     * @param orgId id of the organisation
     * @param personalTaxId id of the personal tax file
     *
     * @returns Observable of calculation that is ready
     */
    createOptimizationAndPollResult(orgId: number, personalTaxId: number): Observable<PersonalTaxOperation> {
        return this.createOptimization(orgId, personalTaxId).pipe(
            delay(DELAY_STATUS_FETCH_MS),
            mergeMap((newOptimization) =>
                this.getOptimization(orgId, personalTaxId, newOptimization.id).pipe(
                    tap((optimization) => {
                        if (optimization.status === PollingStatus.Init || optimization.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    /**
     * Create new import and poll result until it is ready
     * @param taxFiles an array of personal tax files
     *
     * @returns An array of observables of imports that are ready
     */
    createImportsAndPollResults(taxFiles: PersonalTaxFile[]): Observable<PersonalTaxImport>[] {
        const imports$ = this.createImports(taxFiles).pipe(shareReplay(1));

        return taxFiles.map((taxFile, index) =>
            imports$.pipe(
                mergeMap((newImports) =>
                    this.getImport(taxFiles[index].organisation, taxFiles[index].id, newImports[index].id).pipe(
                        tap((imp) => {
                            if (imp.status === PollingStatus.Init || imp.status === PollingStatus.InProgress) {
                                throw Error(invalidErrorMessage);
                            }
                        }),
                        retry({
                            delay: (error) => {
                                if (error.message !== invalidErrorMessage) {
                                    throw error;
                                } else {
                                    return of(true).pipe(delay(LONG_DELAY_STATUS_FETCH_MS));
                                }
                            },
                        }),
                    ),
                ),
            ),
        );
    }

    /**
     * Create new export and poll result until it is ready
     * @param orgId id of the organisation
     * @param personalTaxId id of the personal tax file
     *
     * @returns Observable of export that is ready
     */
    createExportAndPollResult(orgId: number, personalTaxId: number, type: ExportType): Observable<PersonalTaxExport> {
        return this.createExport(orgId, personalTaxId, type).pipe(
            delay(DELAY_STATUS_FETCH_MS),
            mergeMap((newExport) =>
                this.getExport(orgId, personalTaxId, newExport.id).pipe(
                    tap((exp) => {
                        if (exp.status === PollingStatus.Init || exp.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    /**
     * Create new bulk export and poll result until it is ready
     * @param orgId id of the organisation
     * @param data export parameter data (has period_id, company_id, reconciliation_id, firm_id)
     *
     * @returns Observable of export that is ready
     */
    createBulkExportAndPollResult(orgId: number, data: ExportParameter[], type: ExportType): Observable<PersonalTaxExport> {
        return this.createBulkExport(orgId, data, type).pipe(
            delay(DELAY_STATUS_FETCH_MS),
            mergeMap((newExport) =>
                this.getBulkExport(orgId, newExport.id).pipe(
                    tap((exp) => {
                        if (exp.status === PollingStatus.Init || exp.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    /**
     * Create new parsing and start polling result
     * @param orgId id of the organisation
     * @param personalTaxId id of the personal tax file
     * @param formData form data containing the relevant file information and type
     *
     * @returns Observable of parsing that is either successful or failed
     */
    createParsingAndPollResult(orgId: number, personalTaxId: number, formData: FormData): Observable<PersonalTaxParsing> {
        return this.createParsing(orgId, personalTaxId, formData).pipe(
            delay(DELAY_STATUS_FETCH_MS),
            mergeMap((newParsing) =>
                this.getParsing(orgId, personalTaxId, newParsing.id).pipe(
                    tap((parsing) => {
                        if (parsing.status !== PollingStatus.Fail && parsing.status !== PollingStatus.Success) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    /**
     * Create new notification and poll result until it is ready
     * @param orgId id of the organisation
     * @param personalTaxId id of the personal tax file
     * @param role role of the user to be notified
     * @returns Observable of notification that is ready
     */
    sendQuestionnaireResultAndPollResult(orgId: number, personalTaxId: number, role?: TaxRole): Observable<TaxNotification> {
        return this.sendQuestionnaireResult(orgId, personalTaxId, role).pipe(
            delay(DELAY_STATUS_FETCH_MS),
            mergeMap((newNotification) =>
                this.getQuestionnaireResult(orgId, personalTaxId, newNotification.id).pipe(
                    tap((notification) => {
                        if (notification.status === PollingStatus.Init || notification.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    /**
     * Retrieve the history of a personal tax file
     * @param orgId id of the organisation
     * @param personalTaxId id of the personal tax file
     *
     * @returns Observable of list of personal tax requests
     */
    getPersonalTaxHistory(orgId: number, personalTaxId: number): Observable<PersonalTaxRequest[]> {
        return this.http
            .get<PersonalTaxRequest[]>(environment.endpoints.organisationPersonalTaxHistory(orgId, personalTaxId))
            .pipe(map<any[], PersonalTaxRequest[]>((data) => data.map((d) => new PersonalTaxRequest().deserialize(d))));
    }
}
